import { chainOrSuspend, pyCallOrSuspend, pyCallable, pyNone, pyStr, pyTrue, toJs } from "@Sk";
import PyDefUtils from "PyDefUtils";
import {
    AnvilHookSpec,
    Component,
    ComponentConstructor,
    DropZone,
    HandleInteraction,
    Interaction,
} from "@runtime/components/Component";
import { SectionUpdates, designerApi } from "@runtime/runner/component-designer-api";
import { getCssPrefix } from "@runtime/runner/legacy-features";
import { anvilServerMod, reportError } from "@runtime/runner/py-util";
import utils from "../../utils";
import { BASE_MODULES } from "../python-environment";

const { DesignDataGrid, DataGrid } = BASE_MODULES["anvil"];

interface ColumnDef {
    id: string;
    title: string;
    data_key: string;
    width?: number;
    expand?: boolean;
}

type SectionElements = Map<string, HTMLDivElement>;

interface RecordType {
    id: string;
    name: string;
    fields: {
        key: string;
        type: "string" | "number" | "bool" | "date" | "datetime" | "media" | "object";
    }[];
}

let loadingTableSchema: boolean = false;
let tableSchema: RecordType[] | null = null;

const loadTableSchema = async () => {
    loadingTableSchema = true;
    try {
        const r = await Sk.misceval.asyncToPromise(() =>
            pyCallOrSuspend(
                anvilServerMod.call as pyCallable,
                [new pyStr("anvil.record_schema.get/anvil.tables")],
                ["_anvil_expose_all_for_designer", pyTrue]
            )
        );
        tableSchema = (toJs(r) as any).record_types;
    } catch (e: any) {
        reportError(e);
    } finally {
        loadingTableSchema = false;
    }
};

export const reloadTableSchemaForDataGrids = async () => {
    if (tableSchema || loadingTableSchema) {
        await loadTableSchema();
    }
};

const mkDesignerHookSpec = (oldHookSpec: AnvilHookSpec): Partial<AnvilHookSpec> => {
    return {
        disableDropMode() {
            const dropZoneElements = this._anvil.dropZoneElements;
            for (const element of dropZoneElements) {
                element.remove();
            }
            dropZoneElements.length = 0;
        },
        enableDropMode(dropping) {
            this.anvil$hooks.disableDropMode!();
            const { childPanel, footerSlot } = this._anvil.elements;
            const { getAutoHeader, isAutoHeader, mkDropzone } = this._anvil.designerHelpers;

            const dzs: DropZone[] = [];
            const autoHeader = getAutoHeader();
            const footerDz = mkDropzone();
            footerSlot.insertAdjacentElement("beforeend", footerDz);

            dzs.push({
                element: footerDz,
                dropInfo: {
                    layout_properties: { slot: "footer" },
                },
            });

            let components: { component: Component; layoutProperties: any }[] = this._anvil.components;
            if (isAutoHeader()) {
                components = components.slice(1);
            }
            components.forEach(({ component, layoutProperties }, childIdx) => {
                if (layoutProperties.slot === "footer") {
                    return;
                }
                const dz = mkDropzone();
                component.anvil$hooks.domElement!.insertAdjacentElement("beforebegin", dz);
                dzs.push({ element: dz, dropInfo: { minChildIdx: childIdx, maxChildIdx: childIdx } });
            });

            const endDz = mkDropzone();
            childPanel.insertAdjacentElement("beforeend", endDz);
            dzs.push({ element: endDz, dropInfo: {} });

            return dzs;
        },
        getInteractions() {
            const { refreshSections, updateColumns, translateHandle } = this._anvil.designerHelpers;

            const oldHandles = this._anvil.getHandles();
            const domElement = this.anvil$hooks.domElement!;
            const interactions: Interaction[] = [
                {
                    type: "whole_component",
                    title: "Add Column",
                    icon: "add",
                    callbacks: {
                        execute() {
                            updateColumns((columns: ColumnDef[]) => {
                                let i = 1;
                                while (columns.some((c) => c.title === "Column " + i)) {
                                    i++;
                                }
                                columns.push({
                                    id: utils.getRandomStr(6),
                                    data_key: "column_" + i,
                                    title: "Column " + i,
                                });
                            });
                            refreshSections();
                        },
                    },
                },
            ];
            if (tableSchema?.length) {
                interactions.push({
                    type: "whole_component_multi",
                    title: "Add Columns From Table",
                    options: tableSchema.map((table) => ({
                        id: table.id,
                        name: `Columns from ${table.name}`,
                        icon: "add",
                    })),
                    callbacks: {
                        execute(id) {
                            const fields = tableSchema?.find((t) => t.id === id)?.fields;
                            if (!fields) {
                                return;
                            }
                            updateColumns((columns: ColumnDef[]) => {
                                for (const field of fields) {
                                    columns.push({
                                        id: utils.getRandomStr(6),
                                        data_key: field.key,
                                        title: field.key,
                                    });
                                }
                            });
                            refreshSections();
                        },
                    },
                });
            }

            return oldHandles.map((h: any) => translateHandle(h, domElement)).concat(interactions);
        },
        getSections() {
            const { describeSectionForColumn, sections } = this._anvil.designerHelpers;
            if (!sections.size) return;
            const columns = this._anvil.getPropJS("columns") as ColumnDef[];
            return columns?.map((c, index) => describeSectionForColumn(columns, c, index));
        },
        getSectionDomElement(id) {
            const { sections } = this._anvil.designerHelpers;
            return sections.get(id);
        },
        setSectionPropertyValues(sectionID, updates) {
            const { updateColumns } = this._anvil.designerHelpers;

            updateColumns((columns: ColumnDef[]) => {
                const column = columns.find(({ id }) => id === sectionID);
                if (column) {
                    Object.assign(column, updates);
                }
                return { updates };
            });
            return true;
        },
    };
};

export const DesignDataGridV3 = Sk.misceval.buildClass(
    BASE_MODULES["anvil"],
    function ($gbl, $loc) {
        $loc["__init__"] = PyDefUtils.funcFastCall(([self]: [Component], kws) =>
            chainOrSuspend(DesignDataGrid.prototype.tp$init.call(self, [], kws), () => {
                const { configHeader, childPanel } = self._anvil.elements;
                const isAutoHeader = () => self._anvil.getPropJS("auto_header");

                /** @todo - if autoHeader is not true then we have no sections so need to do something else (probably use configHeader - make this work!) */
                const getAutoHeader = () =>
                    isAutoHeader()
                        ? (childPanel as HTMLDivElement).firstElementChild!
                        : (configHeader as HTMLDivElement);

                const sections: SectionElements = new Map();
                const getSections = () => {
                    for (const element of [...(getAutoHeader()?.children ?? [])]) {
                        const id = element.getAttribute("data-grid-col-id");
                        if (!id) continue;
                        sections.set(id, element as HTMLDivElement);
                    }
                };
                getSections();
                const updateSections = () => {
                    sections.clear();
                    getSections();
                };
                const refreshSections = () => {
                    updateSections();
                    designerApi.updateComponentSections(self);
                };

                const setupPaddingWhenAutoHeaderNotPresent = () => {
                    const ah = isAutoHeader();
                    self._anvil.element.css("padding-top", !ah ? "15px" : "initial");
                    self._anvil.elements.configHeader.style.marginTop = !ah ? "-15px" : "initial";
                };

                setupPaddingWhenAutoHeaderNotPresent();

                self._anvil.onUpdateAutoHeader = () => {
                    refreshSections();
                    setupPaddingWhenAutoHeaderNotPresent();
                };

                self._anvil.afterUpdateColumns = () => {
                    refreshSections();
                };

                const dropZoneElements: Element[] = [];
                self._anvil.dropZoneElements = dropZoneElements;

                const mkDropzone = () => {
                    const element = document.createElement("div");
                    element.setAttribute("anvil-flow-panel-dropzone", "true");
                    dropZoneElements.push(element);
                    return element;
                };

                const describeSectionForColumn = (
                    columns: ColumnDef[],
                    { id, title, data_key, width, expand }: ColumnDef,
                    index: number
                ) => ({
                    id,
                    title,
                    element: sections.get(id)!,
                    propertyDescriptions: [
                        { name: "title", type: "string" },
                        { name: "data_key", type: "string" },
                        { name: "width", type: "string" },
                        { name: "expand", type: "boolean" },
                    ],
                    propertyValues: { title, data_key, width, expand },
                    interactions: [
                        ...(isAutoHeader()
                            ? [
                                  {
                                      type: "whole_component",
                                      title: "Edit title",
                                      icon: "edit",
                                      callbacks: {
                                          execute() {
                                              designerApi.startInlineEditing(
                                                  self,
                                                  { name: "title", type: "string" },
                                                  sections.get(id)!.querySelector(`.${getCssPrefix()}label-text`)!,
                                                  { sectionId: id }
                                              );
                                          },
                                      },
                                      default: true,
                                  },
                              ]
                            : []),
                        ...self._anvil
                            .getHandles()
                            .filter((h: any) => h.colId === id)
                            .map((h: any) => translateHandle(h, sections.get(id)!)),
                        ...(index === 0
                            ? []
                            : [
                                  {
                                      type: "whole_component",
                                      title: "Move left",
                                      icon: "arrow_left",
                                      callbacks: {
                                          execute() {
                                              updateColumns((columns) => {
                                                  const x = columns[index];
                                                  columns[index] = columns[index - 1];
                                                  columns[index - 1] = x;
                                              });
                                              designerApi.updateComponentSections(self);
                                          },
                                      },
                                  },
                              ]),
                        ...(index === columns.length - 1
                            ? []
                            : [
                                  {
                                      type: "whole_component",
                                      title: "Move right",
                                      icon: "arrow_right",
                                      callbacks: {
                                          execute() {
                                              updateColumns((columns) => {
                                                  const x = columns[index];
                                                  columns[index] = columns[index + 1];
                                                  columns[index + 1] = x;
                                              });
                                              designerApi.updateComponentSections(self);
                                          },
                                      },
                                  },
                              ]),
                        {
                            type: "whole_component",
                            title: "Delete",
                            icon: "delete",
                            callbacks: {
                                execute() {
                                    updateColumns((columns) => ({ columns: columns.filter((c) => c.id !== id) }));
                                    refreshSections();
                                },
                            },
                        },
                    ] as Interaction[],
                });

                const updateColumns = (
                    update: (columns: ColumnDef[]) => { columns?: ColumnDef[]; updates?: SectionUpdates } | void
                ) => {
                    let columns = [...(self._anvil.getPropJS("columns") || [])] as ColumnDef[];
                    const r = update(columns);
                    if (r?.columns) {
                        columns = r.columns;
                    }
                    self._anvil.setPropJS("columns", columns);

                    designerApi.updateComponentProperties(self, { columns }, {});
                    return r?.updates || true;
                };

                const translateHandle = (h: any, domElement: HTMLElement): HandleInteraction => {
                    const { x, y, width, height } = h;
                    const elementBounds = domElement.getBoundingClientRect();
                    let grabData: any = null;
                    return {
                        type: "handle",
                        x: x - elementBounds.x - (document.scrollingElement?.scrollLeft || 0) + 4,
                        y: y - elementBounds.y - (document.scrollingElement?.scrollTop || 0) + 4,
                        width,
                        height,
                        direction: "x",
                        callbacks: {
                            grab: () => {
                                grabData = self._anvil.handleGrab(h, 0, 0);
                            },
                            drag: (dx, dy, metaKey) => {
                                const handle = self._anvil.handleDrag(h, grabData, dx, dy, metaKey);
                                const elementBounds = domElement.getBoundingClientRect();
                                return {
                                    x: handle.x - elementBounds.x - (document.scrollingElement?.scrollLeft || 0) + 4,
                                    y: handle.y - elementBounds.y - (document.scrollingElement?.scrollTop || 0) + 4,
                                };
                            },
                            drop: (dx, dy) => {
                                const props = self._anvil.handleDrop(h, grabData, dx, dy);
                                if (!props) return;
                                const propertyUpdates: any = props.properties["undefined"]; // Because the component doesn't know its own name anymore.
                                designerApi.updateComponentProperties(self, propertyUpdates, {});
                                designerApi.notifyInteractionsChanged(self);
                            },
                            doubleClick: () => {
                                const props = self._anvil.handleDoubleClick(h);
                                if (!props) return;
                                const propertyUpdates: any = props.properties["undefined"]; // Because the component doesn't know its own name anymore.
                                self._anvil.setPropJS("columns", propertyUpdates["columns"]);
                                designerApi.updateComponentProperties(self, propertyUpdates, {});
                                designerApi.notifyInteractionsChanged(self);
                            },
                        },
                    };
                };

                if (!tableSchema && !loadingTableSchema) {
                    loadingTableSchema = true;
                    void loadTableSchema();
                }

                self._anvil.designerHelpers = {
                    getAutoHeader,
                    isAutoHeader,
                    sections,
                    refreshSections,
                    setupPaddingWhenAutoHeaderNotPresent,
                    mkDropzone,
                    describeSectionForColumn,
                    updateColumns,
                    translateHandle,
                };

                return pyNone;
            })
        );

        const oldHookSpec = DataGrid.prototype.anvil$hookSpec;
        $loc.anvil$hookSpec = Object.assign(Object.create(oldHookSpec), mkDesignerHookSpec(oldHookSpec));
    },
    "DesignDataGridV3",
    [DesignDataGrid]
) as ComponentConstructor;
